import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from 'contexts/ToastContext'
import { useAuth } from 'contexts/AuthContext'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Badge } from 'components/ui/Badge'
import {
  Filter,
  Brain,
  Star,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  FileText,
  Percent,
  X,
  ChevronDown,
  AlertTriangle
} from 'lucide-react'
import { candidatesApi, applicationsApi, offersApi, hiringProcessApi, type Candidate, type HiringStep, type Offer } from 'lib/api'
import { RecommendedSkillsModal, type RecommendedSkillsData } from 'features/offers/components/RecommendedSkillsModal'
import { buildRecommendedSkillsDataFromAiResponse } from 'lib/offerRecommendedSkillsUtils'
import { useNotifications } from 'contexts/NotificationContext'

interface SkillFilterItem {
  name: string
  weight: number
}

interface FilterState {
  experienceYears: number | null
  hardSkills: SkillFilterItem[]
  softSkills: SkillFilterItem[]
  certifications: SkillFilterItem[]
  languages: SkillFilterItem[]
  educationLevel: { level: string, weight: number } | null
}

interface AIMatchingModalProps {
  isOpen: boolean
  onClose: () => void
  offerId: number
  offerTitle: string
  offerStatus?: 'draft' | 'published' | 'offer_closed' | 'closed' | 'pending' | 'accepted' | 'declined' | 'expired'
  onScoresUpdated?: () => void
  minimumScore?: number
  onMinimumScoreChange?: (score: number) => void
  onOfferPublished?: () => void
}

interface MatchingBreakdownCategory {
  score: number
  weight?: number
  weighted_score?: number
  matched?: string[]
  missing?: string[]
  details?: any // For experience_years and other detailed breakdowns
}

interface MatchingCandidate extends Candidate {
  matchScore?: number
  isSelected: boolean
  matchingSkills?: string[]
  reasons?: string[]
  scoreBreakdown?: Record<string, MatchingBreakdownCategory> | null
  passesFilters?: boolean
  filterReasons?: string[]
}

export function AIMatchingModal({
  isOpen,
  onClose,
  offerId,
  offerTitle,
  offerStatus,
  onScoresUpdated,
  minimumScore: propMinimumScore,
  onMinimumScoreChange,
  onOfferPublished
}: AIMatchingModalProps) {
  const { t, i18n } = useTranslation()
  const { addNotification } = useNotifications()
  const { addToast } = useToast()
  const { user } = useAuth()
  const isSuperUser = (user as any)?.is_superuser || false

  const [candidates, setCandidates] = useState<MatchingCandidate[]>([])
  const [selectedCandidates, setSelectedCandidates] = useState<number[]>([])
  const [internalMinimumScore, setInternalMinimumScore] = useState<number>(70)
  const [fetchedMinimumScore, setFetchedMinimumScore] = useState<number | null>(null) // Store fetched value to prioritize over stale prop
  const [isPublishing, setIsPublishing] = useState(false)
  const [showRecommendedSkillsModal, setShowRecommendedSkillsModal] = useState(false)
  const [recommendedSkillsData, setRecommendedSkillsData] = useState<RecommendedSkillsData>({})
  const [publishedOfferForSkills, setPublishedOfferForSkills] = useState<Offer | null>(null)
  const [isSavingRecommendedSkills, setIsSavingRecommendedSkills] = useState(false)
  const [showDraftWarning, setShowDraftWarning] = useState(false)
  const [showClosedWarning, setShowClosedWarning] = useState(false)
  const [showZeroWeightsWarning, setShowZeroWeightsWarning] = useState(false)
  const [currentOfferStatus, setCurrentOfferStatus] = useState<'draft' | 'published' | 'offer_closed' | 'closed' | 'pending' | 'accepted' | 'declined' | 'expired' | undefined>(offerStatus)

  // Use fetched value if available (most up-to-date), otherwise use prop if provided, otherwise use internal state
  const minimumScore = fetchedMinimumScore !== null ? fetchedMinimumScore : (propMinimumScore !== undefined ? propMinimumScore : internalMinimumScore)

  const [localScore, setLocalScore] = useState<string>(String(Math.floor(minimumScore)))
  const [hasInteracted, setHasInteracted] = useState(false)

  // Sync local score when minimumScore changes (from prop, fetched value, or internal state)
  useEffect(() => {
    setLocalScore(String(Math.floor(minimumScore)))
  }, [minimumScore])

  // Update current offer status when prop changes
  useEffect(() => {
    setCurrentOfferStatus(offerStatus)
  }, [offerStatus])

  // Check if offer is draft / closed when modal opens
  useEffect(() => {
    if (!isOpen) {
      setShowDraftWarning(false)
      setShowClosedWarning(false)
      return
    }
    if (currentOfferStatus === 'offer_closed' || currentOfferStatus === 'closed') {
      setShowClosedWarning(true)
      setShowDraftWarning(false)
    } else if (currentOfferStatus === 'draft') {
      setShowDraftWarning(true)
      setShowClosedWarning(false)
    } else {
      setShowDraftWarning(false)
      setShowClosedWarning(false)
    }
  }, [isOpen, currentOfferStatus])

  // Handle publishing the offer
  const handlePublishOffer = async () => {
    // Check if offer weights sum to 0
    const weights = offerData?.ai_matching_weights || {}
    const sumWeights = Object.values(weights).reduce((sum: number, val: any) => sum + (Number(val) || 0), 0)
    if (sumWeights === 0) {
      setShowZeroWeightsWarning(true)
      return
    }

    try {
      setIsPublishing(true)
      const response = await offersApi.update(offerId, { status: 'published' })
      let freshOffer = response.data
      let freshAiResponse = freshOffer?.ai_response
      if (!freshAiResponse) {
        try {
          const freshResponse = await offersApi.getById(offerId)
          freshOffer = freshResponse.data
          freshAiResponse = freshOffer?.ai_response
        } catch {
          // ignore – modal is a bonus feature
        }
      }
      // Update local status
      setCurrentOfferStatus('published')
      setShowDraftWarning(false)
      if (freshOffer) {
        setOfferData(freshOffer)
      }
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('aiMatching.offerPublished', 'Offer published successfully'),
        duration: 3000
      })
      if (onOfferPublished) {
        onOfferPublished()
      }
      const recommendedSkills = buildRecommendedSkillsDataFromAiResponse(freshAiResponse)
      if (recommendedSkills && freshOffer) {
        setRecommendedSkillsData(recommendedSkills)
        setPublishedOfferForSkills(freshOffer)
        setShowRecommendedSkillsModal(true)
      }
    } catch (error: any) {
      console.error('Error publishing offer:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: error.response?.data?.error || t('aiMatching.offerPublishFailed', 'Failed to publish offer'),
        duration: 4000
      })
    } finally {
      setIsPublishing(false)
    }
  }

  const handleAcceptRecommendedSkills = async (
    selected: {
      hard_skills_non_mentionnées: string[]
      soft_skills_non_mentionnées: string[]
      nice_to_have_non_mentionnées: string[]
    },
    weights?: {
      hard_skills: number
      soft_skills: number
      nice_to_have: number
    },
    skillWeights?: Record<string, number>
  ) => {
    if (!publishedOfferForSkills) return
    setIsSavingRecommendedSkills(true)
    try {
      const payload: Record<string, unknown> = {
        requirements: {
          hard_skills_mentionnées: publishedOfferForSkills.requirements?.hard_skills_mentionnées ?? [],
          hard_skills_non_mentionnées: selected.hard_skills_non_mentionnées
        },
        soft_skills: {
          soft_skills_mentionnées: publishedOfferForSkills.soft_skills?.soft_skills_mentionnées ?? [],
          soft_skills_non_mentionnées: selected.soft_skills_non_mentionnées
        },
        benefits: {
          nice_to_have_mentionnées: publishedOfferForSkills.benefits?.nice_to_have_mentionnées ?? [],
          nice_to_have_non_mentionnées: selected.nice_to_have_non_mentionnées
        }
      }
      if (weights) {
        payload.ai_matching_weights = {
          ...(publishedOfferForSkills.ai_matching_weights || {}),
          ...weights
        }
      }
      if (skillWeights) {
        payload.skill_weights = skillWeights
      }
      const response = await offersApi.updateAiSkills(publishedOfferForSkills.id, payload as any)
      setOfferData(response.data)
      setShowRecommendedSkillsModal(false)
      setPublishedOfferForSkills(null)
      if (onOfferPublished) {
        onOfferPublished()
      }
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('recommendedSkills.successMessage', 'Les compétences sélectionnées ont été ajoutées avec succès à l’offre.')
      })
    } catch (err) {
      console.error('Error saving recommended skills:', err)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('recommendedSkills.errorMessage', 'Impossible d’enregistrer les compétences.')
      })
    } finally {
      setIsSavingRecommendedSkills(false)
    }
  }

  // Wrapper function to handle score updates with error handling
  const handleScoreChange = async (newScore: number) => {
    // Store the original score from offer (head of scanning) to restore on error
    const originalScore = minimumScore || 70

    if (onMinimumScoreChange) {
      try {
        // Call the update function (which should be async and return a Promise)
        await onMinimumScoreChange(newScore)
        // If successful, update local score AND internal state so minimumScore reflects the new value
        setLocalScore(newScore.toString())
        setInternalMinimumScore(newScore)
        setFetchedMinimumScore(newScore) // Also update fetched score to ensure it takes precedence
      } catch (error) {
        // If update fails, restore to original score from offer (head of scanning)
        // This ensures the number returns to the original value when error shows
        setLocalScore(originalScore.toString())
        // Show translated error message
        addToast({
          type: 'error',
          title: t('aiMatching.scoreLimitError', 'Score Limit'),
          message: t('aiMatching.cannotIncreaseScore', { score: originalScore, defaultValue: `Cannot increase score above ${originalScore}% because matching has already been run for this offer.` }),
          duration: 4000
        })
      }
    } else {
      setInternalMinimumScore(newScore)
      setLocalScore(newScore.toString())
    }
  }
  const [appliedApplicationIds, setAppliedApplicationIds] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [matching, setMatching] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const [matchingResults, setMatchingResults] = useState<MatchingCandidate[]>([])
  const [offerData, setOfferData] = useState<Offer | null>(null)
  const [applyStatus, setApplyStatus] = useState<{ type: 'success' | 'warning' | 'error'; message: string } | null>(null)
  const [hasMatchingRun, setHasMatchingRun] = useState<boolean>(false)
  const [showConfirmApplyModal, setShowConfirmApplyModal] = useState(false)
  const [confirmApplyMode, setConfirmApplyMode] = useState<'score' | 'top_k' | 'top_k_tie'>('score')
  const [confirmApplyScore, setConfirmApplyScore] = useState<number>(70)
  const [confirmApplyTopK, setConfirmApplyTopK] = useState<number>(10)
  const [topKTieChoices, setTopKTieChoices] = useState<Set<number>>(new Set())
  const [hiringSteps, setHiringSteps] = useState<HiringStep[]>([])
  // Frontend-only matching mode — never persisted to backend
  // 'top_k' is only available during the first matching (hasMatchingRun === false)
  const [matchingMode, setMatchingMode] = useState<'score' | 'top_k'>('score')
  const [localTopK, setLocalTopK] = useState<string>('10')
  const appliedCount = useMemo(() => {
    if (!offerData?.candidates_by_step) return 0
    // Try to find the step corresponding to 'applied' status
    // Usually it's the first step ("Initial Application"), but we search for keywords to be robust
    const initialStep = offerData.candidates_by_step.find(s =>
      s.step_name.toLowerCase().includes('initial') ||
      s.step_name.toLowerCase().includes('applied') ||
      s.step_name.toLowerCase().includes('candidat')
    )
    return initialStep ? initialStep.count : 0
  }, [offerData])

  // Filter states
  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const [filters, setFilters] = useState<FilterState>({
    experienceYears: null,
    hardSkills: [],
    softSkills: [],
    certifications: [],
    languages: [],
    educationLevel: null
  })
  const [selectedHardSkill, setSelectedHardSkill] = useState('')
  const [selectedSoftSkill, setSelectedSoftSkill] = useState('')
  const [selectedCertification, setSelectedCertification] = useState('')
  const [selectedLanguage, setSelectedLanguage] = useState('')

  const handleCancel = () => {
    // Clear matching results without saving to database
    // Candidates remain in their original 'applied' status
    setMatchingResults([])
    setApplyStatus(null)
    setShowConfirmApplyModal(false)
    // Clear fetched score so it doesn't persist stale data
    setFetchedMinimumScore(null)
    // Close the modal without triggering score updates
    onClose()
  }

  // Format filters for API
  const formatFiltersForAPI = (): Offer['filter_matching'] => {
    const formattedFilters: NonNullable<Offer['filter_matching']> = {}

    // Preserve stage_exclu from current offer data so it is never wiped on save
    const existingStageExclu = offerData?.filter_matching?.stage_exclu
    if (existingStageExclu !== undefined && existingStageExclu !== null) {
      formattedFilters.stage_exclu = existingStageExclu
    }

    const existingInitialMatchingMode = offerData?.filter_matching?.initial_matching_mode
    if (existingInitialMatchingMode) {
      formattedFilters.initial_matching_mode = existingInitialMatchingMode
    }

    const existingTopK = offerData?.filter_matching?.top_k
    if (existingTopK !== undefined && existingTopK !== null) {
      formattedFilters.top_k = existingTopK
    }

    // Experience
    if (filters.experienceYears !== null && filters.experienceYears > 0) {
      formattedFilters.experience = filters.experienceYears
    }

    // Hard Skills
    if (filters.hardSkills.length > 0) {
      formattedFilters.hard_skills = filters.hardSkills.reduce((acc, skill) => {
        acc[skill.name] = Math.round(skill.weight * 100) // Convert 0-1 to 0-100
        return acc
      }, {} as Record<string, number>)
    }

    // Soft Skills
    if (filters.softSkills.length > 0) {
      formattedFilters.soft_skills = filters.softSkills.reduce((acc, skill) => {
        acc[skill.name] = Math.round(skill.weight * 100)
        return acc
      }, {} as Record<string, number>)
    }

    // Certifications
    if (filters.certifications.length > 0) {
      formattedFilters.certifications = filters.certifications.reduce((acc, cert) => {
        acc[cert.name] = Math.round(cert.weight * 100)
        return acc
      }, {} as Record<string, number>)
    }

    // Languages
    if (filters.languages.length > 0) {
      formattedFilters.languages = filters.languages.reduce((acc, lang) => {
        acc[lang.name] = Math.round(lang.weight * 100)
        return acc
      }, {} as Record<string, number>)
    }

    // Education Level
    if (filters.educationLevel) {
      formattedFilters.education_level = {
        level: filters.educationLevel.level,
        similarity: Math.round(filters.educationLevel.weight * 100)
      }
    }

    return formattedFilters
  }

  // Save filters to backend
  const saveFiltersToBackend = async () => {
    try {
      const formattedFilters = formatFiltersForAPI()
      await offersApi.update(offerId, { filter_matching: formattedFilters })
    } catch (error) {
      console.error('Failed to save filters:', error)
    }
  }

  // Load saved filters from offer data
  const loadSavedFilters = (offerData: any) => {
    if (!offerData?.filter_matching) return

    const savedFilters = offerData.filter_matching
    const newFilters: FilterState = {
      experienceYears: savedFilters.experience || null,
      hardSkills: [],
      softSkills: [],
      certifications: [],
      languages: [],
      educationLevel: null
    }

    // Load hard skills
    if (savedFilters.hard_skills) {
      newFilters.hardSkills = Object.entries(savedFilters.hard_skills).map(([name, similarity]) => ({
        name,
        weight: (similarity as number) / 100 // Convert 0-100 to 0-1
      }))
    }

    // Load soft skills
    if (savedFilters.soft_skills) {
      newFilters.softSkills = Object.entries(savedFilters.soft_skills).map(([name, similarity]) => ({
        name,
        weight: (similarity as number) / 100
      }))
    }

    // Load certifications
    if (savedFilters.certifications) {
      newFilters.certifications = Object.entries(savedFilters.certifications).map(([name, similarity]) => ({
        name,
        weight: (similarity as number) / 100
      }))
    }

    // Load languages
    if (savedFilters.languages) {
      newFilters.languages = Object.entries(savedFilters.languages).map(([name, similarity]) => ({
        name,
        weight: (similarity as number) / 100
      }))
    }

    // Load education level
    if (savedFilters.education_level) {
      newFilters.educationLevel = {
        level: savedFilters.education_level.level,
        weight: savedFilters.education_level.similarity / 100
      }
    }

    setFilters(newFilters)
  }

  useEffect(() => {
    if (isOpen) {
      fetchData()
    }
  }, [isOpen, offerId])

  // Auto-save filters when they change
  useEffect(() => {
    if (offerData && isOpen) {
      // Debounce save to avoid too many API calls
      const timeoutId = setTimeout(() => {
        saveFiltersToBackend()
      }, 500)

      return () => clearTimeout(timeoutId)
    }
  }, [filters, offerData, isOpen])

  // Reset matching mode to 'score' if matching has already run
  useEffect(() => {
    if (hasMatchingRun) {
      setMatchingMode('score')
    }
  }, [hasMatchingRun])

  const fetchData = async () => {
    try {
      setLoading(true)

      // Fetch latest offer data to get current minimum_score and step counts
      const offerResponse = await offersApi.getById(offerId)
      const currentOffer = offerResponse.data
      setOfferData(currentOffer)

      const stepsResponse = await hiringProcessApi.getStepsForOffer(offerId)
      const stepsData = Array.isArray(stepsResponse.data)
        ? stepsResponse.data
        : ((stepsResponse.data as any)?.results ?? [])
      setHiringSteps(stepsData)

      // Load saved filters
      loadSavedFilters(currentOffer)

      // Sync minimum score with latest value from database
      if (currentOffer.minimum_score !== undefined) {
        const latestScore = currentOffer.minimum_score
        setFetchedMinimumScore(latestScore)
        setInternalMinimumScore(latestScore)
        setLocalScore(latestScore.toString())
      }

      // Optimization: We no longer fetch all candidates here to avoid performance issues.
      // The summary count of candidates in 'applied' status is already in currentOffer.candidates_by_step.
      setHasMatchingRun(!!currentOffer.has_matching_run)

      // We will fetch the actual candidate IDs just-in-time when "Start Matching" is clicked.
      setCandidates([])
      setSelectedCandidates([])
    } catch (error) {
      console.error('Error fetching data:', error)
      addNotification({
        type: 'alert',
        title: t('common.error'),
        message: t('aiMatching.fetchError'),
        priority: 'high',
        category: 'aiMatching'
      })
    } finally {
      setLoading(false)
    }
  }

  const getAllSelectedCandidates = (): Candidate[] => {
    return candidates.filter(c => selectedCandidates.includes(c.id))
  }

  // Validate minimum score before starting matching or applying results
  // Returns the validated score if valid, null otherwise
  const validateMinimumScore = (): number | null => {
    const inputValue = localScore
    if (inputValue === '' || isNaN(Number(inputValue))) {
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('aiMatching.invalidScore', 'Invalid score value')
      })
      const fallbackScore = minimumScore || 70
      setLocalScore(fallbackScore.toString())
      return null
    }

    const numValue = Number(inputValue)
    const validatedScore = Math.max(0, Math.min(100, numValue))
    const currentScore = minimumScore || 70

    // Check if trying to increase score when matching has been run
    if (hasMatchingRun && validatedScore > currentScore) {
      addToast({
        type: 'error',
        title: t('aiMatching.scoreLimitError', 'Score Limit'),
        message: t('aiMatching.cannotIncreaseScore', { score: currentScore, defaultValue: `Minimum score cannot exceed ${currentScore}%` }),
        duration: 4000
      })
      setLocalScore(currentScore.toString())
      return null
    }

    // Update local score if it was clamped
    if (validatedScore !== numValue) {
      setLocalScore(validatedScore.toString())
    }

    return validatedScore
  }

  const handleStartMatching = async () => {
    if (currentOfferStatus === 'offer_closed' || currentOfferStatus === 'closed') {
      addToast({
        type: 'warning',
        title: t('offerDetail.closedOffer', 'Offer closed'),
        message: t('offerDetail.cannotLaunchMatchingClosed', 'AI matching is disabled while this offer is closed.'),
      })
      return
    }
    // Validate score before starting
    const validatedScore = validateMinimumScore()
    if (validatedScore === null) {
      return
    }

    try {
      setMatching(true)

      // JIT Fetching: Get candidate IDs for 'applied' status just before matching
      // This allows the modal to open instantly without waiting for a large application list
      const applicationsResponse = await applicationsApi.getByOffer(offerId, { page_size: 1000, summary: true })
      const appliedApplications = (applicationsResponse.data.applications || []).filter(app => app.status === 'applied')
      const candidateIds = appliedApplications.map(app => app.candidate)

      if (candidateIds.length === 0) {
        addToast({
          type: 'warning',
          title: t('common.info'),
          message: t('aiMatching.noAppliedCandidates', 'No candidates in "applied" status found for this offer.')
        })
        return
      }

      // Call actual AI matching API with the fetched candidate IDs
      const response = await applicationsApi.aiMatching({
        candidate_ids: candidateIds,
        offer_id: offerId
      })

      if (response.data.success && response.data.results) {
        // Map API results to component format and deduplicate by candidate ID
        const seenCandidateIds = new Set<number>()
        const matchingResults = response.data.results
          .map(result => ({
            ...result.candidate,
            matchScore: result.match_score ?? 0,
            isSelected: false,
            matchingSkills: result.matching_skills ?? [],
            reasons: result.reasons ?? [],
            scoreBreakdown: result.score_breakdown ?? null
          }))
          .filter(candidate => {
            // Deduplicate: keep only the first occurrence of each candidate ID
            if (seenCandidateIds.has(candidate.id)) {
              return false
            }
            seenCandidateIds.add(candidate.id)
            return true
          })

        setMatchingResults(matchingResults)
        // NOTE: Do NOT call onScoresUpdated here - scores are not saved to database yet
        // Only call it after "Apply Results" is clicked

        // Show appropriate notification based on whether this is a new or updated matching
        const newMatchCount = response.data.new_matches
        if (newMatchCount !== undefined) {
          // This was an update to existing matching
          addNotification({
            type: 'update',
            title: t('common.success'),
            message: t('aiMatching.matchingUpdated', {
              newCount: newMatchCount,
              totalCount: matchingResults.length,
              defaultValue: `Added ${newMatchCount} new matches. Total: ${matchingResults.length} candidates`
            }),
            priority: 'medium',
            category: 'aiMatching'
          })
        } else if (response.data.message) {
          // No new candidates to match
          addNotification({
            type: 'update',
            title: t('common.info'),
            message: response.data.message,
            priority: 'medium',
            category: 'aiMatching'
          })
        } else {
          // New matching job
          addNotification({
            type: 'update',
            title: t('common.success'),
            message: t('aiMatching.matchingCompleted', { count: matchingResults.length }),
            priority: 'medium',
            category: 'aiMatching'
          })
        }
      } else {
        throw new Error('AI matching API returned unsuccessful response')
      }
    } catch (error) {
      console.error('Error during matching:', error)
      addNotification({
        type: 'alert',
        title: t('common.error'),
        message: t('aiMatching.matchingFailed'),
        priority: 'high',
        category: 'aiMatching'
      })
    } finally {
      setMatching(false)
    }
  }

  const handleApplyMatching = async () => {

    if (matchingMode === 'top_k') {
      // Top-K mode: check if there are any top-K candidates
      if (topKCandidateIds.size === 0 && matchingResults.length > 0) {
        const validTopK = Math.max(1, Math.min(matchingResults.length, parseInt(localTopK) || 10))
        setConfirmApplyMode('top_k')
        setConfirmApplyTopK(validTopK)
        setShowConfirmApplyModal(true)
        return
      }

      const validTopK = Math.max(1, Math.min(matchingResults.length, parseInt(localTopK) || 10))
      const eligibleResults = matchingResults
        .filter(candidate => !filteredOutCandidateIds.has(candidate.id))
        .sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0))
      const lastSelected = eligibleResults[validTopK - 1]
      const nextCandidate = eligibleResults[validTopK]
      if (lastSelected && nextCandidate && (lastSelected.matchScore ?? 0) === (nextCandidate.matchScore ?? 0)) {
        setConfirmApplyMode('top_k_tie')
        setConfirmApplyTopK(validTopK)
        setTopKTieChoices(new Set(
          topKTieCandidates
            .filter(candidate => topKCandidateIds.has(candidate.id))
            .map(candidate => candidate.id)
        ))
        setShowConfirmApplyModal(true)
        return
      }
    } else {
      // Score mode: validate score before applying
      const validatedScore = validateMinimumScore()
      if (validatedScore === null) {
        return
      }

      // Use validated score for filtering
      const eligibleResults = matchingResults.filter(
        candidate => !filteredOutCandidateIds.has(candidate.id)
      )
      const qualifiedCandidates = eligibleResults.filter(
        candidate => candidate.matchScore && candidate.matchScore >= validatedScore
      )

      // Check if all candidates are below the score threshold
      if (qualifiedCandidates.length === 0 && matchingResults.length > 0) {
        // Show confirmation modal before proceeding
        setConfirmApplyMode('score')
        setConfirmApplyScore(validatedScore)
        setShowConfirmApplyModal(true)
        return
      }
    }

    // Proceed with applying results
    await applyMatchingResults()
  }

  const applyMatchingResults = async () => {
    setShowConfirmApplyModal(false)

    let passedCandidateIds: Set<number>
    let scoreForPersist: number | null = null  // only set in score mode

    if (matchingMode === 'top_k') {
      const validTopK = Math.max(1, Math.min(matchingResults.length, parseInt(localTopK) || 10))
      const eligibleResults = matchingResults
        .filter(candidate => !filteredOutCandidateIds.has(candidate.id))
        .sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0))
      const boundaryScore = eligibleResults[validTopK - 1]?.matchScore

      if (confirmApplyMode === 'top_k_tie' && boundaryScore !== undefined) {
        // Keep higher scores and only the candidates chosen among the boundary tie.
        passedCandidateIds = new Set(
          eligibleResults
            .filter(candidate => (candidate.matchScore ?? 0) > boundaryScore)
            .map(candidate => candidate.id)
        )
        topKTieChoices.forEach(candidateId => passedCandidateIds.add(candidateId))
      } else {
        passedCandidateIds = new Set(topKCandidateIds)
      }

      const selectedMatchingResults = matchingResults.filter(c => passedCandidateIds.has(c.id))
      if (selectedMatchingResults.length > 0) {
        scoreForPersist = Math.min(...selectedMatchingResults.map(c => c.matchScore ?? 0))
      }
    } else {
      // Score mode: validate the threshold and persist it
      const validatedScore = validateMinimumScore()
      if (validatedScore === null) return
      scoreForPersist = validatedScore
      passedCandidateIds = new Set(
        matchingResults
          .filter(c => !filteredOutCandidateIds.has(c.id) && (c.matchScore ?? 0) >= validatedScore)
          .map(c => c.id)
      )
    }

    try {
      setApplyStatus(null)
      setIsApplying(true)

      // Persist minimum_score BEFORE moving applications.
      // Once candidates leave "applied" (or get ai_decision + match_score), the backend
      // treats matching as applied and rejects increasing minimum_score above the old value.
      // Saving the threshold first avoids that race on the initial matching apply.
      if (scoreForPersist !== null) {
        const modePayload: Partial<import('lib/api').Offer> = { minimum_score: scoreForPersist }
        const updatedFilterMatching = { ...(offerData?.filter_matching || {}) }

        if (matchingMode === 'top_k' && !hasMatchingRun) {
          // First matching with Top-K: persist note for Shortlist IA footer
          updatedFilterMatching.initial_matching_mode = 'top_k'
          updatedFilterMatching.top_k = Math.max(1, parseInt(localTopK) || 10)
          modePayload.filter_matching = updatedFilterMatching
        } else if (hasMatchingRun && updatedFilterMatching.initial_matching_mode === 'top_k') {
          // Second+ matching: hide Top-K first-match note
          delete updatedFilterMatching.initial_matching_mode
          delete updatedFilterMatching.top_k
          modePayload.filter_matching = updatedFilterMatching
        }

        await offersApi.update(offerId, modePayload)
        setLocalScore(scoreForPersist.toString())
        setInternalMinimumScore(scoreForPersist)
        setFetchedMinimumScore(scoreForPersist)
      }

      // Get all applications for the offer (summary mode)
      const applicationsResponse = await applicationsApi.getByOffer(offerId, { page_size: 500, summary: true })
      const applications = applicationsResponse.data.applications || []
      const firstStepIdByType = hiringSteps.reduce<Record<string, number>>((acc, step) => {
        if (acc[step.step_type] === undefined) {
          acc[step.step_type] = step.id
        }
        return acc
      }, {})

      // Process updates in chunks
      const chunkSize = 10
      const updateResults: PromiseSettledResult<any>[] = []

      for (let i = 0; i < matchingResults.length; i += chunkSize) {
        const chunk = matchingResults.slice(i, i + chunkSize)
        const chunkPromises = chunk.map(async (candidate) => {
          const score = candidate.matchScore ?? 0
          const passed = passedCandidateIds.has(candidate.id)
          const targetStatus: 'screening' | 'scanning' = passed ? 'screening' : 'scanning'
          const aiDecision: 'passed_by_ai' | 'failed_by_ai' = passed ? 'passed_by_ai' : 'failed_by_ai'
          const targetStepId = firstStepIdByType[targetStatus] ?? null

          const existingApps = applications.filter(app => (app.candidate === candidate.id) || ((app.candidate as any)?.id === candidate.id))

          if (existingApps.length > 0) {
            return Promise.all(existingApps.map(existingApp => applicationsApi.update(existingApp.id, {
              status: targetStatus,
              current_step: targetStepId,
              match_score: score,
              ai_decision: aiDecision,
            } as any)))
          }

          return applicationsApi.create({
            candidate: candidate.id,
            offer: offerId,
            status: targetStatus,
            current_step: targetStepId,
            match_score: score,
            ai_decision: aiDecision,
          } as any)
        })

        const results = await Promise.allSettled(chunkPromises)
        updateResults.push(...results)
      }

      const successful = updateResults.filter(r => r.status === 'fulfilled').length
      const failed = updateResults.filter(r => r.status === 'rejected').length
      console.log('=== APPLICATION UPDATES COMPLETED ===', 'Successful:', successful, 'Failed:', failed)

      onScoresUpdated?.()

      const successMessage = t('aiMatching.candidatesMoved', { count: passedCandidateIds.size })
      addNotification({
        type: 'update',
        title: t('common.success'),
        message: successMessage,
        priority: 'medium',
        category: 'aiMatching'
      })
      setApplyStatus({ type: 'success', message: successMessage })

      onClose()
    } catch (error) {
      console.error('Error applying matching:', error)
      const errorMessage = t('aiMatching.applyFailed')
      addNotification({
        type: 'alert',
        title: t('common.error'),
        message: errorMessage,
        priority: 'high',
        category: 'aiMatching'
      })
      setApplyStatus({ type: 'error', message: errorMessage })
    } finally {
      setIsApplying(false)
    }
  }

  const getScoreColor = (score: number) => {
    if (matchingMode === 'top_k' && matchingResults.length > 0) {
      // Top-K mode: highlight top-K candidates by score
      const validTopK = Math.max(1, Math.min(matchingResults.length, parseInt(localTopK) || 10))
      const sortedScores = [...matchingResults]
        .filter(c => !filteredOutCandidateIds.has(c.id))
        .map(c => c.matchScore ?? 0)
        .sort((a, b) => b - a)
      const kthScore = sortedScores[validTopK - 1] ?? 0
      if (score > 0 && score >= kthScore) {
        return 'text-green-600 bg-green-100 dark:bg-green-900/20'
      }
      return 'text-red-600 bg-red-100 dark:bg-red-900/20'
    }
    // Score mode: compare score with current highlight threshold
    if (score >= currentHighlightThreshold) {
      return 'text-green-600 bg-green-100 dark:bg-green-900/20'
    }
    return 'text-red-600 bg-red-100 dark:bg-red-900/20'
  }

  // Handle filter logic
  const toggleFilterPanel = () => {
    setShowFilterPanel(!showFilterPanel)
  }

  const handleExperienceChange = (value: string) => {
    const numValue = Number(value)
    setFilters((prev) => ({
      ...prev,
      experienceYears: numValue <= 0 ? null : numValue
    }))
  }

  const addSkillFilter = (type: 'hardSkills' | 'softSkills' | 'certifications' | 'languages', skill: string) => {
    if (!skill) return
    setFilters((prev) => {
      if (prev[type].some((item) => item.name === skill)) {
        return prev
      }
      return {
        ...prev,
        [type]: [...prev[type], { name: skill, weight: 0.5 }]
      }
    })
  }

  const removeSkillFilter = (type: 'hardSkills' | 'softSkills' | 'certifications' | 'languages', skill: string) => {
    setFilters((prev) => ({
      ...prev,
      [type]: prev[type].filter((item) => item.name !== skill)
    }))
  }

  const updateSkillWeight = (type: 'hardSkills' | 'softSkills' | 'certifications' | 'languages', skill: string, weight: number) => {
    setFilters((prev) => ({
      ...prev,
      [type]: prev[type].map((item) =>
        item.name === skill ? { ...item, weight } : item
      )
    }))
  }

  // Normalize similarity score based on skill weights
  const getNormalizedSimilarity = (skillName: string, similarity: number, type: 'hard_skills' | 'soft_skills') => {
    if (!offerData?.skill_weights || !skillName) {
      return Math.min(1, similarity)
    }

    const skillWeights = type === 'hard_skills'
      ? offerData.skill_weights.hard_skills
      : offerData.skill_weights.soft_skills

    if (!skillWeights) {
      return Math.min(1, similarity)
    }

    const weight = skillWeights[skillName]
    if (weight === undefined || weight === 0) {
      // No weight defined, use original normalized similarity
      return Math.min(1, similarity)
    }

    // Divide similarity by weight to normalize it
    const normalizedSimilarity = similarity / weight
    return Math.min(1, normalizedSimilarity)
  }

  const handleClearFilters = () => {
    setFilters({
      experienceYears: null,
      hardSkills: [],
      softSkills: [],
      certifications: [],
      languages: [],
      educationLevel: null
    })
    setSelectedHardSkill('')
    setSelectedSoftSkill('')
    setSelectedCertification('')
    setSelectedLanguage('')
  }

  const hasActiveFilters =
    (filters.experienceYears !== null && filters.experienceYears > 0) ||
    filters.hardSkills.length > 0 ||
    filters.softSkills.length > 0 ||
    filters.certifications.length > 0 ||
    filters.languages.length > 0 ||
    filters.educationLevel !== null

  const offerHardSkills = useMemo(() => {
    const translated = i18n.language === 'en' ? offerData?.translation_en?.hard_skills || [] : []
    const requirements = Array.isArray(offerData?.requirements) ? offerData.requirements : []
    return Array.from(new Set([...translated, ...requirements].filter((item: string) => item && item.trim())))
  }, [offerData, i18n.language])

  const offerSoftSkills = useMemo(() => {
    const translated = i18n.language === 'en' ? offerData?.translation_en?.soft_skills || [] : []
    const base = Array.isArray(offerData?.soft_skills) ? offerData.soft_skills : []
    return Array.from(new Set([...translated, ...base].filter((item: string) => item && item.trim())))
  }, [offerData, i18n.language])

  const offerCertifications = useMemo(() => {
    const translated = i18n.language === 'en' ? offerData?.translation_en?.certifications || [] : []
    const base = Array.isArray(offerData?.certifications) ? offerData.certifications : []
    return Array.from(new Set([...translated, ...base].filter((item: string) => item && item.trim())))
  }, [offerData, i18n.language])

  const offerExperienceMax = useMemo(() => {
    const rawValue = offerData?.experience_level
    if (!rawValue) return 0
    const matches = String(rawValue).match(/\d+(?:\.\d+)?/g)
    if (!matches || matches.length === 0) return 0
    const parsed = Math.max(...matches.map((value) => Number(value)))
    return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0
  }, [offerData])

  const offerLanguages = useMemo(() => {
    const translated = i18n.language === 'en' ? offerData?.translation_en?.languages || [] : []
    const base = Array.isArray(offerData?.languages) ? offerData.languages : []
    const combined = [...translated, ...base]
    const seen = new Set<string>()
    return combined
      .map((lang) => {
        const language = typeof lang === 'string' ? lang : (lang.language || ('name' in lang ? lang.name : '') || '')
        const level = typeof lang === 'string' ? '' : (lang.level || '')
        return { language, level }
      })
      .filter((lang) => {
        if (!lang.language || !lang.level) return false
        const key = `${lang.language}-${lang.level}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      .map((lang) => `${lang.language} (${lang.level})`)
  }, [offerData, i18n.language])

  const offerEducationLevels = useMemo(() => {
    const levels = []
    if (offerData?.education_level) {
      levels.push(offerData.education_level)
    }
    // Add standard education levels as additional options
    return Array.from(new Set([
      ...levels,
      'Bac',
      'Bac +2',
      'Bac +3',
      'Bac +5',
      'Bac +8',
      'Doctorat'
    ]))
  }, [offerData])

  // Helper function to generate filter reasons based on user role
  const getFilterReason = (filterType: 'experience' | 'hardSkills' | 'softSkills' | 'certifications' | 'languages' | 'education', detailedReason?: string): string => {
    if (isSuperUser && detailedReason) {
      return detailedReason
    }

    // Standard reasons for non-superusers
    const standardReasons: Record<string, string> = {
      'experience': t('aiMatching.filterReason.experience', 'Eliminated by years of experience'),
      'hardSkills': t('aiMatching.filterReason.hardSkills', 'Eliminated by required skills'),
      'softSkills': t('aiMatching.filterReason.softSkills', 'Eliminated by soft skills'),
      'certifications': t('aiMatching.filterReason.certifications', 'Eliminated by certifications'),
      'languages': t('aiMatching.filterReason.languages', 'Eliminated by languages'),
      'education': t('aiMatching.filterReason.education', 'Eliminated by education level'),
    }

    return standardReasons[filterType] || 'Eliminated by filter'
  }

  const filteredMatchingResults = useMemo(() => {
    // Instead of filtering candidates out, we keep all and add metadata about filter status
    return matchingResults.map((candidate) => {
      const passesFilters = !hasActiveFilters
      const filterReasons: string[] = []

      if (!hasActiveFilters) {
        return { ...candidate, passesFilters: true, filterReasons: [] }
      }

      const scoreBreakdown = candidate.scoreBreakdown
      const activeHardSkills = filters.hardSkills.filter((item) => item.weight > 0)
      const activeSoftSkills = filters.softSkills.filter((item) => item.weight > 0)
      const activeCertifications = filters.certifications.filter((item) => item.weight > 0)
      const activeLanguages = filters.languages.filter((item) => item.weight > 0)

      let candidatePassesFilters = true

      // Check experience filter
      if (filters.experienceYears !== null && filters.experienceYears > 0) {
        const experienceYears = scoreBreakdown?.experience_years?.details?.cv_experience_pertinente
        let years: number | null = experienceYears ?? null

        if (years === null) {
          const match = candidate.experience?.match(/(\d+(?:\.\d+)?)/)
          years = match ? Number(match[1]) : null
        }

        if (years === null || years < filters.experienceYears) {
          candidatePassesFilters = false
          const detailedReason = isSuperUser ? `Expérience: ${years ?? 0}/${filters.experienceYears} ans requis` : undefined
          filterReasons.push(getFilterReason('experience', detailedReason))
        }
      }

      // Check hard skills filter
      if (activeHardSkills.length > 0) {
        const details = scoreBreakdown?.hard_skills?.details
        const missingSkills: string[] = []

        if (details && Array.isArray(details)) {
          const similarityMap = new Map<string, number>()
          details.forEach((detail: any) => {
            if (detail.job_competence && detail.similarity !== undefined) {
              const normalizedSimilarity = getNormalizedSimilarity(detail.job_competence, detail.similarity, 'hard_skills')
              similarityMap.set(detail.job_competence.toLowerCase(), normalizedSimilarity)
            }
          })

          activeHardSkills.forEach((item) => {
            const similarity = similarityMap.get(item.name.toLowerCase())
            if (similarity === undefined || similarity < item.weight) {
              missingSkills.push(`${item.name} (${Math.round((similarity ?? 0) * 100)}%/${Math.round(item.weight * 100)}%)`)
            }
          })
        } else {
          const candidateHardSkillsFallback = new Set(
            [...(candidate.skills || []), ...(candidate.matchingSkills || [])].map((skill) => skill.toLowerCase())
          )
          activeHardSkills.forEach((item) => {
            if (!candidateHardSkillsFallback.has(item.name.toLowerCase())) {
              missingSkills.push(item.name)
            }
          })
        }

        if (missingSkills.length > 0) {
          candidatePassesFilters = false
          const detailedReason = isSuperUser ? `Hard skills: ${missingSkills.join(', ')}` : undefined
          filterReasons.push(getFilterReason('hardSkills', detailedReason))
        }
      }

      // Check soft skills filter
      if (activeSoftSkills.length > 0) {
        const details = scoreBreakdown?.soft_skills?.details
        const missingSkills: string[] = []

        if (details && Array.isArray(details)) {
          const similarityMap = new Map<string, number>()
          details.forEach((detail: any) => {
            if (detail.job_soft_skill && detail.similarity !== undefined) {
              const normalizedSimilarity = getNormalizedSimilarity(detail.job_soft_skill, detail.similarity, 'soft_skills')
              similarityMap.set(detail.job_soft_skill.toLowerCase(), normalizedSimilarity)
            }
          })

          activeSoftSkills.forEach((item) => {
            const similarity = similarityMap.get(item.name.toLowerCase())
            if (similarity === undefined || similarity < item.weight) {
              missingSkills.push(`${item.name} (${Math.round((similarity ?? 0) * 100)}%/${Math.round(item.weight * 100)}%)`)
            }
          })
        } else {
          const candidateSoftSkillsFallback = new Set((candidate.soft_skills || []).map((skill) => skill.toLowerCase()))
          activeSoftSkills.forEach((item) => {
            if (!candidateSoftSkillsFallback.has(item.name.toLowerCase())) {
              missingSkills.push(item.name)
            }
          })
        }

        if (missingSkills.length > 0) {
          candidatePassesFilters = false
          const detailedReason = isSuperUser ? `Soft skills: ${missingSkills.join(', ')}` : undefined
          filterReasons.push(getFilterReason('softSkills', detailedReason))
        }
      }

      // Check certifications filter
      if (activeCertifications.length > 0) {
        const details = scoreBreakdown?.certifications?.details
        const missingCerts: string[] = []

        if (details && Array.isArray(details)) {
          const similarityMap = new Map<string, number>()
          details.forEach((detail: any) => {
            if (detail.job_certification && detail.similarity !== undefined) {
              const normalizedSimilarity = Math.min(1, detail.similarity)
              similarityMap.set(detail.job_certification.toLowerCase(), normalizedSimilarity)
            }
          })

          activeCertifications.forEach((item) => {
            const similarity = similarityMap.get(item.name.toLowerCase())
            if (similarity === undefined || similarity < item.weight) {
              missingCerts.push(`${item.name} (${Math.round((similarity ?? 0) * 100)}%/${Math.round(item.weight * 100)}%)`)
            }
          })
        } else {
          const candidateCertsFallback = new Set((candidate.certifications || []).map((cert) => cert.toLowerCase()))
          activeCertifications.forEach((item) => {
            if (!candidateCertsFallback.has(item.name.toLowerCase())) {
              missingCerts.push(item.name)
            }
          })
        }

        if (missingCerts.length > 0) {
          candidatePassesFilters = false
          const detailedReason = isSuperUser ? `Certifications: ${missingCerts.join(', ')}` : undefined
          filterReasons.push(getFilterReason('certifications', detailedReason))
        }
      }

      // Check languages filter
      if (activeLanguages.length > 0) {
        const details = scoreBreakdown?.languages?.details
        const missingLangs: string[] = []

        if (details && Array.isArray(details)) {
          const similarityMap = new Map<string, number>()
          details.forEach((detail: any) => {
            if (detail.job_langue && detail.job_niveau && detail.similarity_lang !== undefined) {
              const key = `${detail.job_langue} (${detail.job_niveau})`.toLowerCase()
              similarityMap.set(key, detail.similarity_lang)
            }
          })

          activeLanguages.forEach((item) => {
            const similarity = similarityMap.get(item.name.toLowerCase())
            if (similarity === undefined || similarity < item.weight) {
              missingLangs.push(`${item.name} (${Math.round((similarity ?? 0) * 100)}%/${Math.round(item.weight * 100)}%)`)
            }
          })
        } else {
          const candidateLanguagesFallback = new Set(
            (candidate.languages || []).map((lang) => `${lang.name} (${lang.level || ''})`.toLowerCase())
          )
          activeLanguages.forEach((item) => {
            if (!candidateLanguagesFallback.has(item.name.toLowerCase())) {
              missingLangs.push(item.name)
            }
          })
        }

        if (missingLangs.length > 0) {
          candidatePassesFilters = false
          const detailedReason = isSuperUser ? `Langues: ${missingLangs.join(', ')}` : undefined
          filterReasons.push(getFilterReason('languages', detailedReason))
        }
      }

      // Check education level filter
      if (filters.educationLevel !== null) {
        const details = scoreBreakdown?.niveau_etude?.details
        if (details && details.similarity !== undefined) {
          const normalizedSimilarity = Math.min(1, details.similarity)
          if (normalizedSimilarity < filters.educationLevel.weight) {
            candidatePassesFilters = false
            const detailedReason = isSuperUser ? `Niveau d'étude: ${Math.round(normalizedSimilarity * 100)}%/${Math.round(filters.educationLevel.weight * 100)}%` : undefined
            filterReasons.push(getFilterReason('education', detailedReason))
          }
        } else {
          const candidateEducationFallback = Array.isArray(candidate.education)
            ? candidate.education.length > 0
            : !!candidate.education
          if (!candidateEducationFallback) {
            candidatePassesFilters = false
            const detailedReason = isSuperUser ? `Niveau d'étude: non renseigné` : undefined
            filterReasons.push(getFilterReason('education', detailedReason))
          }
        }
      }

      return {
        ...candidate,
        passesFilters: candidatePassesFilters,
        filterReasons,
        matchScore: candidate.matchScore
      }
    }).sort((a, b) => {
      // First, sort by filter status: candidates passing filters come first
      if (a.passesFilters && !b.passesFilters) return -1
      if (!a.passesFilters && b.passesFilters) return 1

      // Then, within each group, sort by score (descending)
      const scoreA = a.matchScore ?? 0
      const scoreB = b.matchScore ?? 0
      return scoreB - scoreA
    })
  }, [matchingResults, filters, hasActiveFilters, offerData])

  const filteredOutCandidateIds = useMemo(() => {
    if (!hasActiveFilters) return new Set<number>()
    return new Set(
      filteredMatchingResults
        .filter((candidate) => candidate.passesFilters === false)
        .map((candidate) => candidate.id)
    )
  }, [filteredMatchingResults, hasActiveFilters])

  // Derived set of top-K candidate IDs (used for highlighting and apply in top_k mode)
  const topKCandidateIds = useMemo(() => {
    if (matchingMode !== 'top_k' || matchingResults.length === 0) return new Set<number>()
    const validTopK = Math.max(1, Math.min(matchingResults.length, parseInt(localTopK) || 10))
    const eligible = matchingResults.filter(c => !filteredOutCandidateIds.has(c.id))
    const sorted = [...eligible].sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0))
    return new Set(sorted.slice(0, validTopK).map(candidate => candidate.id))
  }, [matchingMode, matchingResults, filteredOutCandidateIds, localTopK])

  const topKTieCandidates = useMemo(() => {
    if (matchingMode !== 'top_k' || matchingResults.length === 0) return []
    const validTopK = Math.max(1, Math.min(matchingResults.length, parseInt(localTopK) || 10))
    const sortedEligible = matchingResults
      .filter(candidate => !filteredOutCandidateIds.has(candidate.id))
      .sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0))
    const boundaryScore = sortedEligible[validTopK - 1]?.matchScore ?? null
    if (boundaryScore === null) return []
    return sortedEligible.filter(candidate => (candidate.matchScore ?? 0) === boundaryScore)
  }, [matchingMode, matchingResults, filteredOutCandidateIds, localTopK])

  const topKTieCandidateIds = useMemo(() => {
    return topKTieCandidates.map(candidate => candidate.id)
  }, [topKTieCandidates])

  const topKTieRequiredChoices = useMemo(() => {
    return topKTieCandidates.filter(candidate => topKCandidateIds.has(candidate.id)).length
  }, [topKTieCandidates, topKCandidateIds])

  const areAllTopKTieCandidatesSelected = topKTieCandidateIds.length > 0
    && topKTieCandidateIds.every(candidateId => topKTieChoices.has(candidateId))

  const currentHighlightThreshold = useMemo(() => {
    return Math.max(0, Math.min(100, Math.floor(Number(localScore) || minimumScore)))
  }, [localScore, minimumScore])

  const displayedMatchingResults = useMemo(() => {
    return [...filteredMatchingResults].sort((a, b) => {
      const aPassesFilters = (a as any).passesFilters !== false
      const bPassesFilters = (b as any).passesFilters !== false
      const aSelected = aPassesFilters && (
        matchingMode === 'top_k'
          ? topKCandidateIds.has(a.id)
          : (a.matchScore || 0) >= currentHighlightThreshold
      )
      const bSelected = bPassesFilters && (
        matchingMode === 'top_k'
          ? topKCandidateIds.has(b.id)
          : (b.matchScore || 0) >= currentHighlightThreshold
      )

      if (aSelected !== bSelected) return aSelected ? -1 : 1
      if (aPassesFilters !== bPassesFilters) return aPassesFilters ? -1 : 1

      const scoreA = a.matchScore ?? 0
      const scoreB = b.matchScore ?? 0
      if (scoreB !== scoreA) return scoreB - scoreA

      return String(a.name ?? a.email ?? '').localeCompare(String(b.name ?? b.email ?? ''), i18n.language || 'fr', { sensitivity: 'base' })
    })
  }, [filteredMatchingResults, matchingMode, topKCandidateIds, currentHighlightThreshold, i18n.language])

  // Calculate filter metadata for each candidate (matching skills, experience years)
  const candidateFilterMetadata = useMemo(() => {
    const metadata = new Map<number, {
      matchedHardSkills: string[],
      matchedSoftSkills: string[],
      matchedCertifications: string[],
      matchedLanguages: string[],
      educationLevel: string | null,
      experienceYears: number | null
    }>()

    filteredMatchingResults.forEach((candidate) => {
      const scoreBreakdown = candidate.scoreBreakdown
      const matchedHardSkills: string[] = []
      const matchedSoftSkills: string[] = []
      const matchedCertifications: string[] = []
      const matchedLanguages: string[] = []
      let educationLevel: string | null = null
      let experienceYears: number | null = null

      // Get experience years if filter is active
      if (filters.experienceYears !== null && filters.experienceYears > 0) {
        experienceYears = scoreBreakdown?.experience_years?.details?.cv_experience_pertinente
        if (experienceYears === undefined || experienceYears === null) {
          // Fallback to parsing candidate.experience
          const match = candidate.experience?.match(/(\d+(?:\.\d+)?)/)
          experienceYears = match ? Number(match[1]) : null
        }
      }

      // Get matched CV skills for active hard skill filters
      const activeHardSkills = filters.hardSkills.filter((item) => item.weight > 0)
      if (activeHardSkills.length > 0 && scoreBreakdown?.hard_skills?.details) {
        const details = scoreBreakdown.hard_skills.details
        if (Array.isArray(details)) {
          activeHardSkills.forEach((filterSkill) => {
            // Find the best matching CV skill for this filter skill
            let bestMatch: { cv_competence: string, similarity: number } | null = null
            details.forEach((detail: any) => {
              if (detail.job_competence?.toLowerCase() === filterSkill.name.toLowerCase() &&
                detail.cv_competence &&
                detail.similarity !== undefined) {
                const normalizedSimilarity = getNormalizedSimilarity(detail.job_competence, detail.similarity, 'hard_skills')
                if (normalizedSimilarity >= filterSkill.weight) {
                  if (!bestMatch || normalizedSimilarity > bestMatch.similarity) {
                    bestMatch = { cv_competence: detail.cv_competence, similarity: normalizedSimilarity }
                  }
                }
              }
            })
            if (bestMatch) {
              matchedHardSkills.push(`${bestMatch.cv_competence} (${(bestMatch.similarity * 100).toFixed(0)}%)`)
            }
          })
        }
      }

      // Get matched CV soft skills for active soft skill filters
      const activeSoftSkills = filters.softSkills.filter((item) => item.weight > 0)
      if (activeSoftSkills.length > 0 && scoreBreakdown?.soft_skills?.details) {
        const details = scoreBreakdown.soft_skills.details
        if (Array.isArray(details)) {
          activeSoftSkills.forEach((filterSkill) => {
            let bestMatch: { cv_soft_skill: string, similarity: number } | null = null
            details.forEach((detail: any) => {
              if (detail.job_soft_skill?.toLowerCase() === filterSkill.name.toLowerCase() &&
                detail.cv_soft_skill &&
                detail.similarity !== undefined) {
                const normalizedSimilarity = getNormalizedSimilarity(detail.job_soft_skill, detail.similarity, 'soft_skills')
                if (normalizedSimilarity >= filterSkill.weight) {
                  if (!bestMatch || normalizedSimilarity > bestMatch.similarity) {
                    bestMatch = { cv_soft_skill: detail.cv_soft_skill, similarity: normalizedSimilarity }
                  }
                }
              }
            })
            if (bestMatch) {
              matchedSoftSkills.push(`${bestMatch.cv_soft_skill} (${(bestMatch.similarity * 100).toFixed(0)}%)`)
            }
          })
        }
      }

      // Get matched CV certifications for active certification filters
      const activeCertifications = filters.certifications.filter((item) => item.weight > 0)
      if (activeCertifications.length > 0 && scoreBreakdown?.certifications?.details) {
        const details = scoreBreakdown.certifications.details
        if (Array.isArray(details)) {
          activeCertifications.forEach((filterCert) => {
            let bestMatch: { cv_certification: string, similarity: number } | null = null
            details.forEach((detail: any) => {
              if (detail.job_certification?.toLowerCase() === filterCert.name.toLowerCase() &&
                detail.cv_certification &&
                detail.similarity !== undefined) {
                // For certifications, use simple normalization (no weight defined)
                const normalizedSimilarity = Math.min(1, detail.similarity)
                if (normalizedSimilarity >= filterCert.weight) {
                  if (!bestMatch || normalizedSimilarity > bestMatch.similarity) {
                    bestMatch = { cv_certification: detail.cv_certification, similarity: normalizedSimilarity }
                  }
                }
              }
            })
            if (bestMatch) {
              matchedCertifications.push(`${bestMatch.cv_certification} (${(bestMatch.similarity * 100).toFixed(0)}%)`)
            }
          })
        }
      }

      // Get matched CV languages for active language filters
      const activeLanguages = filters.languages.filter((item) => item.weight > 0)
      if (activeLanguages.length > 0 && scoreBreakdown?.languages?.details) {
        const details = scoreBreakdown.languages.details
        if (Array.isArray(details)) {
          activeLanguages.forEach((filterLang) => {
            let bestMatch: { cv_langue: string, cv_niveau: string, similarity_lang: number } | null = null
            details.forEach((detail: any) => {
              const jobLangKey = detail.job_langue && detail.job_niveau
                ? `${detail.job_langue} (${detail.job_niveau})`.toLowerCase()
                : ''

              if (jobLangKey === filterLang.name.toLowerCase() &&
                detail.cv_langue &&
                detail.similarity_lang !== undefined) {
                const normalizedSimilarity = Math.min(1, detail.similarity_lang)
                if (normalizedSimilarity >= filterLang.weight) {
                  if (!bestMatch || normalizedSimilarity > bestMatch.similarity_lang) {
                    bestMatch = {
                      cv_langue: detail.cv_langue,
                      cv_niveau: detail.cv_niveau || '',
                      similarity_lang: normalizedSimilarity
                    }
                  }
                }
              }
            })
            if (bestMatch) {
              matchedLanguages.push(`${bestMatch.cv_langue} ${bestMatch.cv_niveau ? `(${bestMatch.cv_niveau})` : ''} - ${(bestMatch.similarity_lang * 100).toFixed(0)}%`)
            }
          })
        }
      }

      // Get education level if filter is active
      if (filters.educationLevel !== null) {
        const details = scoreBreakdown?.niveau_etude?.details
        if (details && details.similarity !== undefined) {
          const normalizedSimilarity = Math.min(1, details.similarity)
          educationLevel = `${filters.educationLevel.level} (${(normalizedSimilarity * 100).toFixed(0)}%)`
        } else {
          // Fallback to candidate.education
          educationLevel = Array.isArray(candidate.education)
            ? candidate.education
                .map((item) => (typeof item === 'string' ? item : item.diplome || item.niveau_etude || ''))
                .filter(Boolean)
                .join(', ')
            : ''
        }
      }

      metadata.set(candidate.id, {
        matchedHardSkills,
        matchedSoftSkills,
        matchedCertifications,
        matchedLanguages,
        educationLevel,
        experienceYears
      })
    })

    return metadata
  }, [filteredMatchingResults, filters, offerData])

  const qualifiedCount = useMemo(() => {
    if (matchingMode === 'top_k') {
      return topKCandidateIds.size
    }
    return matchingResults.filter(candidate => {
      const isFilteredOut = filteredOutCandidateIds.has(candidate.id)
      const score = candidate.matchScore ?? 0
      return score >= currentHighlightThreshold && !isFilteredOut
    }).length
  }, [matchingResults, filteredOutCandidateIds, currentHighlightThreshold, matchingMode, topKCandidateIds])

  const renderFilterContent = () => (
    <div className="space-y-2">
      {/* Years of Experience Filter */}
      <div className="pb-2">
        <div className="flex items-center justify-between w-full p-2">
          <span className="font-medium text-sm text-foreground">
            {t('aiMatching.yearsOfExperienceFilter', 'Years of Experience')}
          </span>
        </div>
        <div className="ml-2 space-y-1 mt-1">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-xs text-muted-foreground">
                {t('aiMatching.minExperience', 'Min')} 0
              </label>
              <span className="text-xs text-muted-foreground">
                {t('aiMatching.maxExperience', 'Max')} {offerExperienceMax}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max={offerExperienceMax}
              step="1"
              value={filters.experienceYears ?? 0}
              onChange={(e) => handleExperienceChange(e.target.value)}
              disabled={offerExperienceMax <= 0}
              className="w-full"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{t('aiMatching.minExperience', 'Min')}</span>
              <span>{filters.experienceYears ?? 0} / {offerExperienceMax}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hard Skills Filter */}
      <div className="pb-2">
        <div className="flex items-center justify-between w-full p-2">
          <span className="font-medium text-sm text-foreground">
            {t('aiMatching.hardSkillsFilter', 'Hard Skills')}
          </span>
        </div>
        <div className="ml-2 space-y-1 mt-1">
          <div className="flex items-center gap-2">
            <select
              value={selectedHardSkill}
              onChange={(e) => setSelectedHardSkill(e.target.value)}
              className="h-8 w-full rounded border border-input bg-background px-2 text-xs"
            >
              <option value="">{t('aiMatching.selectSkill', 'Select a skill')}</option>
              {offerHardSkills.map((skill: string) => (
                <option key={skill} value={skill} disabled={filters.hardSkills.some((item) => item.name === skill)}>
                  {skill}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!selectedHardSkill}
              onClick={() => {
                addSkillFilter('hardSkills', selectedHardSkill)
                setSelectedHardSkill('')
              }}
            >
              +
            </Button>
          </div>
          {filters.hardSkills.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {t('aiMatching.noSkillsSelected', 'No skills selected')}
            </p>
          )}
          <div className="space-y-1">
            {filters.hardSkills.map((item) => (
              <div key={item.name} className="rounded border p-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground">{item.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSkillFilter('hardSkills', item.name)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <div className="mt-1">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Similarité min:</span>
                    <span className="font-medium">{item.weight.toFixed(2)}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={item.weight}
                    onChange={(e) => updateSkillWeight('hardSkills', item.name, Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Soft Skills Filter */}
      <div className="pb-2">
        <div className="flex items-center justify-between w-full p-2">
          <span className="font-medium text-sm text-foreground">
            {t('aiMatching.softSkillsFilter', 'Soft Skills')}
          </span>
        </div>
        <div className="ml-2 space-y-1 mt-1">
          <div className="flex items-center gap-2">
            <select
              value={selectedSoftSkill}
              onChange={(e) => setSelectedSoftSkill(e.target.value)}
              className="h-8 w-full rounded border border-input bg-background px-2 text-xs"
            >
              <option value="">{t('aiMatching.selectSkill', 'Select a skill')}</option>
              {offerSoftSkills.map((skill: string) => (
                <option key={skill} value={skill} disabled={filters.softSkills.some((item) => item.name === skill)}>
                  {skill}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!selectedSoftSkill}
              onClick={() => {
                addSkillFilter('softSkills', selectedSoftSkill)
                setSelectedSoftSkill('')
              }}
            >
              +
            </Button>
          </div>
          {filters.softSkills.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {t('aiMatching.noSkillsSelected', 'No skills selected')}
            </p>
          )}
          <div className="space-y-1">
            {filters.softSkills.map((item) => (
              <div key={item.name} className="rounded border p-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground">{item.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSkillFilter('softSkills', item.name)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <div className="mt-1">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Similarité min:</span>
                    <span className="font-medium">{item.weight.toFixed(2)}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={item.weight}
                    onChange={(e) => updateSkillWeight('softSkills', item.name, Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Certifications Filter */}
      <div className="pb-2">
        <div className="flex items-center justify-between w-full p-2">
          <span className="font-medium text-sm text-foreground">
            {t('aiMatching.certificationsFilter', 'Certifications')}
          </span>
        </div>
        <div className="ml-2 space-y-1 mt-1">
          <div className="flex items-center gap-2">
            <select
              value={selectedCertification}
              onChange={(e) => setSelectedCertification(e.target.value)}
              className="h-8 w-full rounded border border-input bg-background px-2 text-xs"
            >
              <option value="">{t('aiMatching.selectSkill', 'Select a skill')}</option>
              {offerCertifications.map((cert: string) => (
                <option key={cert} value={cert} disabled={filters.certifications.some((item) => item.name === cert)}>
                  {cert}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!selectedCertification}
              onClick={() => {
                addSkillFilter('certifications', selectedCertification)
                setSelectedCertification('')
              }}
            >
              +
            </Button>
          </div>
          {filters.certifications.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {t('aiMatching.noSkillsSelected', 'No skills selected')}
            </p>
          )}
          <div className="space-y-1">
            {filters.certifications.map((item) => (
              <div key={item.name} className="rounded border p-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground">{item.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSkillFilter('certifications', item.name)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <div className="mt-1">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Similarité min:</span>
                    <span className="font-medium">{item.weight.toFixed(2)}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={item.weight}
                    onChange={(e) => updateSkillWeight('certifications', item.name, Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Languages Filter */}
      <div className="pb-2">
        <div className="flex items-center justify-between w-full p-2">
          <span className="font-medium text-sm text-foreground">
            {t('aiMatching.languagesFilter', 'Languages')}
          </span>
        </div>
        <div className="ml-2 space-y-1 mt-1">
          <div className="flex items-center gap-2">
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="h-8 w-full rounded border border-input bg-background px-2 text-xs"
            >
              <option value="">{t('aiMatching.selectLanguage', 'Select a language')}</option>
              {offerLanguages.map((lang: string) => (
                <option key={lang} value={lang} disabled={filters.languages.some((item) => item.name === lang)}>
                  {lang}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!selectedLanguage}
              onClick={() => {
                addSkillFilter('languages', selectedLanguage)
                setSelectedLanguage('')
              }}
            >
              +
            </Button>
          </div>
          {filters.languages.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {t('aiMatching.noLanguagesSelected', 'No languages selected')}
            </p>
          )}
          <div className="space-y-1">
            {filters.languages.map((item) => (
              <div key={item.name} className="rounded border p-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground">{item.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSkillFilter('languages', item.name)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <div className="mt-1">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Similarité min:</span>
                    <span className="font-medium">{item.weight.toFixed(2)}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={item.weight}
                    onChange={(e) => updateSkillWeight('languages', item.name, Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Education Level Filter */}
      <div className="pb-2">
        <div className="flex items-center justify-between w-full p-2">
          <span className="font-medium text-sm text-foreground">
            {t('aiMatching.educationLevelFilter', 'Niveau d\'étude')}
          </span>
        </div>
        {offerData?.education_level && (
          <div className="ml-2 space-y-2 mt-1">
            <div className="rounded border p-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-foreground">{offerData.education_level}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFilters({ ...filters, educationLevel: filters.educationLevel ? null : { level: offerData.education_level, weight: 0.5 } })}
                  className="h-6 px-2"
                >
                  {filters.educationLevel ? 'Désactiver' : 'Activer'}
                </Button>
              </div>
              {filters.educationLevel && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Similarité min:</span>
                    <span className="font-medium">{filters.educationLevel.weight.toFixed(2)}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={filters.educationLevel.weight}
                    onChange={(e) => {
                      if (filters.educationLevel) {
                        setFilters({
                          ...filters,
                          educationLevel: { ...filters.educationLevel, weight: Number(e.target.value) }
                        })
                      }
                    }}
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {!offerData?.education_level && (
          <div className="ml-2 text-xs text-muted-foreground p-2">
            {t('aiMatching.noEducationLevel', 'Aucun niveau d\'étude requis pour cette offre')}
          </div>
        )}
      </div>

      {/* Clear Filters Button */}
      <div className="flex flex-col space-y-2 pt-4 border-t border-border">
        <Button
          className="w-full h-8 text-xs"
          disabled={!hasActiveFilters}
          onClick={handleClearFilters}
          variant="outline"
        >
          {t('aiMatching.clearFilters', 'Clear Filters')}
        </Button>
      </div>
    </div>
  )

  return (
    <>
      {/* Container for both modals when filter panel is open */}
      {isOpen && (
        <div className="fixed inset-0 z-[100]">
          {/* Shared Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={matchingResults.length > 0 ? handleCancel : onClose}
          />

          <div className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none">
            <div className={`flex gap-4 pointer-events-auto ${showFilterPanel ? '' : 'justify-center w-full'}`}>
              {/* Filter Panel Modal - Desktop only */}
              {isSuperUser && showFilterPanel && (
                <div className="hidden lg:flex w-full max-w-sm bg-background border shadow-lg rounded-lg overflow-hidden flex-col max-h-[75vh]">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 sm:p-6 border-b flex-shrink-0">
                    <h2 className="text-lg sm:text-xl font-semibold">{t('aiMatching.filterPanel', 'Filter Panel')}</h2>
                    <Button variant="ghost" size="sm" onClick={() => setShowFilterPanel(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Filter Content */}
                  <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    {renderFilterContent()}
                  </div>
                </div>
              )}

              {/* Main AI Matching Modal */}
              <div className={`bg-background border shadow-lg rounded-lg overflow-hidden flex flex-col max-h-[75vh] ${showFilterPanel ? 'w-full max-w-3xl' : 'w-full max-w-3xl'}`}>
                {/* Header */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-b flex-shrink-0">
                  <h2 className="text-lg sm:text-xl font-semibold">{t('aiMatching.title', 'AI Matching')}</h2>
                  <Button variant="ghost" size="sm" onClick={matchingResults.length > 0 ? handleCancel : onClose}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <Brain className="h-6 w-6 text-blue-600" />
                        <div>
                          <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                            {t('aiMatching.forOffer', 'AI Matching for')}: {offerTitle}
                          </h3>
                          <p className="text-sm text-blue-700 dark:text-blue-300">
                            {t('aiMatching.description', 'Select candidates or teams to run AI matching and automatically move qualified candidates to the offer step.')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Closed / Clôturée Status Warning */}
                    {showClosedWarning && (
                      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <h3 className="font-semibold text-orange-900 dark:text-orange-100 mb-1">
                              {t('aiMatching.closedWarningTitle', 'Offer closed')}
                            </h3>
                            <p className="text-sm text-orange-700 dark:text-orange-300 mb-4">
                              {t('aiMatching.closedWarningMessage', 'AI matching is not available for closed or archived offers. Reactivate the offer to run matching again.')}
                            </p>
                            <Button
                              variant="outline"
                              onClick={onClose}
                              className="border-orange-300 text-orange-700 hover:bg-orange-100 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-900/30"
                            >
                              {t('common.close', 'Close')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Draft Status Warning */}
                    {showDraftWarning && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <FileText className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <h3 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                              {t('aiMatching.draftWarningTitle', 'Draft Offer Detected')}
                            </h3>
                            <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-4">
                              {t('aiMatching.draftWarningMessage', 'AI matching is only available for published offers. Please publish this offer first to proceed with AI matching.')}
                            </p>
                            <div className="flex space-x-3">
                              <Button
                                onClick={handlePublishOffer}
                                disabled={isPublishing}
                                className="bg-yellow-600 hover:bg-yellow-700 text-white"
                              >
                                {isPublishing ? (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    {t('aiMatching.publishing', 'Publishing...')}
                                  </>
                                ) : (
                                  t('aiMatching.publishOffer', 'Publish Offer')
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                onClick={onClose}
                                disabled={isPublishing}
                                className="border-yellow-300 text-yellow-700 hover:bg-yellow-100 dark:border-yellow-700 dark:text-yellow-300 dark:hover:bg-yellow-900/30"
                              >
                                {t('common.cancel', 'Cancel')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Configuration - Matching Mode + Score/TopK Input + Filter Button */}
                    {!showDraftWarning && !showClosedWarning && (
                      <>
                        <div className="rounded-lg border p-4 bg-background border-border space-y-3">

                          {/* Mode toggle — only visible on first matching */}


                          {/* Configuration - Score Input or Top-K Input + Filter Button */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Filter className="h-4 w-4 text-blue-600" />
                              {matchingMode === 'top_k' && !hasMatchingRun ? (
                                <>
                                  <span className="text-sm font-medium">
                                    {t('aiMatching.topKLabel', 'Top-K candidats')}
                                  </span>
                                  <Input
                                    type="number"
                                    min="1"
                                    max="500"
                                    step="1"
                                    value={localTopK}
                                    onChange={(e) => setLocalTopK(e.target.value)}
                                    className="h-7 w-21 pl-2 pr-2 text-sm border bg-white dark:bg-gray-800 text-gray-900 dark:text-white [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    style={{ MozAppearance: 'textfield' }}
                                    placeholder="10"
                                  />
                                </>
                              ) : (
                                <>
                                  <span className="text-sm font-medium">
                                    {t('aiMatching.minimumScore', 'Score minimum')}
                                  </span>
                                  <div className="relative">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
                                      <Percent className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                                    </div>
                                    <Input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="1"
                                      value={localScore}
                                      onChange={(e) => { setLocalScore(e.target.value.replace(/\D/g, '')); setHasInteracted(true); }}
                                      onKeyDown={(e) => {
                                        if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) {
                                          e.preventDefault()
                                        }
                                      }}
                                      className="h-7 w-21 pl-7 pr-2 text-sm border bg-white dark:bg-gray-800 text-gray-900 dark:text-white [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                      style={{ MozAppearance: 'textfield' }}
                                      placeholder="70"
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="flex items-center space-x-3">
                              {/* Mode toggle — only visible on first matching */}
                              {!loading && !hasMatchingRun && matchingResults.length === 0 && (
                                <div className="flex rounded-md overflow-hidden border border-input">
                                  <button
                                    type="button"
                                    onClick={() => setMatchingMode('score')}
                                    className={`px-3 py-1 text-xs font-medium transition-colors ${matchingMode === 'score'
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-background text-foreground hover:bg-muted'
                                      }`}
                                  >
                                    {t('aiMatching.modeScore', 'Score min.')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setMatchingMode('top_k')}
                                    className={`px-3 py-1 text-xs font-medium transition-colors border-l border-input ${matchingMode === 'top_k'
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-background text-foreground hover:bg-muted'
                                      }`}
                                  >
                                    {t('aiMatching.modeTopK', 'Top-K')}
                                  </button>
                                </div>
                              )}

                              {/* Filter Button - Visible when results are shown and user is superuser */}
                              {isSuperUser && matchingResults.length > 0 && (
                                <Button
                                  variant="outline"
                                  onClick={toggleFilterPanel}
                                  className="flex items-center space-x-2 h-7"
                                  title={t('aiMatching.filters', 'Filters')}
                                >
                                  <Filter className="h-4 w-4" />
                                  <span className="text-xs">{t('aiMatching.filterButton', 'Filter')}</span>
                                  {hasActiveFilters && <Badge variant="destructive" className="h-4 w-4 p-0 flex items-center justify-center text-xs">!</Badge>}
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Mode description */}
                          <p className="text-xs text-muted-foreground">
                            {matchingMode === 'top_k' && !hasMatchingRun
                              ? t('aiMatching.modeTopKDesc', 'The top {{k}} candidates will be sent to preselection.', { k: parseInt(localTopK) || 10 })
                              : t('aiMatching.modeScoreDesc', 'Les candidats dont le score est supérieur au seuil seront envoyés en présélection.')}
                          </p>

                          {/* Warning if matching has been run and score cannot be increased */}
                          {hasMatchingRun && minimumScore !== undefined && (
                            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                              <div className="flex items-start space-x-2">
                                <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                                    {t('aiMatching.scoreLimitWarning', 'Score Limit Warning')}
                                  </p>
                                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                                    {t('aiMatching.cannotIncreaseScoreNote', {
                                      score: minimumScore,
                                      defaultValue: `You cannot increase the minimum score above ${minimumScore}% because matching has already been run for this offer.`
                                    })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          {loading && (
                            <div className="flex items-center space-x-2 bg-blue-50/50 dark:bg-blue-900/10 p-3 rounded-lg border border-blue-100 dark:border-blue-800/50">
                              <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />
                              <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                {t('aiMatching.loadingCandidatesStatus', 'Loading candidates with the selected status...')}
                              </p>
                            </div>
                          )}
                          {!loading && appliedCount > 0 && matchingResults.length === 0 && (
                            <p className="text-sm text-muted-foreground">
                              {t('aiMatching.candidatesSelected', {
                                count: appliedCount,
                                defaultValue: `${appliedCount} candidates in "applied" status will be matched`,
                              })}
                            </p>
                          )}
                          {!loading && appliedCount === 0 && matchingResults.length === 0 && (
                            <p className="text-sm text-yellow-600 dark:text-yellow-400">
                              {t('aiMatching.noAppliedCandidates', 'No candidates in "applied" status found for this offer.')}
                            </p>
                          )}
                        </div>

                        {/* Filter Panel - Mobile/Tablet inline */}
                        {isSuperUser && showFilterPanel && matchingResults.length > 0 && (
                          <div className="lg:hidden rounded-lg border p-4 bg-background border-border">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-semibold text-sm">{t('aiMatching.filterPanel', 'Filter Panel')}</h4>
                              <Button variant="ghost" size="sm" onClick={() => setShowFilterPanel(false)} className="h-6 w-6 p-0">
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            {renderFilterContent()}
                          </div>
                        )}

                        {/* Compact Matching Results */}
                        {matchingResults.length > 0 && (
                          <div className="rounded-lg border p-4 bg-background border-border">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-2">
                                <div className="p-1.5 bg-green-100 rounded-lg">
                                  <Star className="h-4 w-4 text-green-600" />
                                </div>
                                <span className="text-lg font-semibold text-foreground">
                                  {t('aiMatching.results', 'Matching Results')}
                                </span>
                              </div>

                              <div className="flex items-center space-x-2">
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 font-bold px-3 py-1">
                                  {t('aiMatching.candidatesSelectedCount', { count: qualifiedCount, defaultValue: `${qualifiedCount} candidates selected` })}
                                </Badge>
                                {hasActiveFilters && (
                                  <Badge variant="secondary" className="text-xs">
                                    {(() => {
                                      const passingCount = filteredMatchingResults.filter((c: any) => c.passesFilters !== false).length
                                      const totalCount = filteredMatchingResults.length
                                      const eliminatedCount = totalCount - passingCount
                                      return `${eliminatedCount} ${t('aiMatching.filterResults', 'ont ete éliminés')}`
                                    })()}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {displayedMatchingResults.map(candidate => {
                                const metadata = candidateFilterMetadata.get(candidate.id)
                                const passesFilters = (candidate as any).passesFilters !== false
                                const filterReasons = (candidate as any).filterReasons || []
                                const displayedScore = passesFilters ? (candidate.matchScore || 0) : 0

                                const isHighlighted = passesFilters && (
                                  matchingMode === 'top_k'
                                    ? topKCandidateIds.has(candidate.id)
                                    : (candidate.matchScore || 0) >= currentHighlightThreshold
                                )

                                return (
                                  <div
                                    key={candidate.id}
                                    className={`flex items-center justify-between p-3 border rounded-lg transition-all ${passesFilters
                                      ? isHighlighted
                                        ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800 shadow-sm'
                                        : 'bg-white border-border hover:bg-muted dark:bg-gray-800'
                                      : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
                                      }`}
                                  >
                                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                                      <div className="flex-1 min-w-0">
                                        <p className={`font-medium text-sm truncate ${!passesFilters ? 'text-red-700 dark:text-red-300' : ''}`}>
                                          {candidate.name}
                                        </p>
                                        <p className="text-xs text-muted-foreground truncate">{candidate.email}</p>

                                        {/* Filter reasons - shown when candidate doesn't pass filters */}
                                        {!passesFilters && filterReasons.length > 0 && (
                                          <div className="mt-2 flex flex-wrap gap-1">
                                            {filterReasons.map((reason: string, idx: number) => (
                                              <Badge key={`reason-${idx}`} variant="outline" className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800 text-xs">
                                                ⚠️ {reason}
                                              </Badge>
                                            ))}
                                          </div>
                                        )}

                                        {/* Matched criteria - shown only for superusers when candidate passes filters */}
                                        {isSuperUser && passesFilters && metadata && (
                                          <div className="mt-2 flex flex-wrap gap-1">
                                            {metadata.experienceYears !== null && (
                                              <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                                                📅 {metadata.experienceYears} {metadata.experienceYears === 1 ? 'an' : 'ans'}
                                              </Badge>
                                            )}
                                            {metadata.matchedHardSkills.map((skill, idx) => (
                                              <Badge key={`hs-${idx}`} variant="outline" className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800 text-xs">
                                                🔧 {skill}
                                              </Badge>
                                            ))}
                                            {metadata.matchedSoftSkills.map((skill, idx) => (
                                              <Badge key={`ss-${idx}`} variant="outline" className="bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800 text-xs">
                                                💡 {skill}
                                              </Badge>
                                            ))}
                                            {metadata.matchedCertifications.map((cert, idx) => (
                                              <Badge key={`c-${idx}`} variant="outline" className="bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800 text-xs">
                                                🎓 {cert}
                                              </Badge>
                                            ))}
                                            {metadata.matchedLanguages.map((lang, idx) => (
                                              <Badge key={`l-${idx}`} variant="outline" className="bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800 text-xs">
                                                🌍 {lang}
                                              </Badge>
                                            ))}
                                            {metadata.educationLevel && (
                                              <Badge variant="outline" className="bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800">
                                                🎯 {metadata.educationLevel}
                                              </Badge>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2 flex-shrink-0">
                                      <Badge className={`${!passesFilters ? 'text-red-600 bg-red-100 dark:bg-red-900/20' : getScoreColor(candidate.matchScore || 0)} text-xs`}>
                                        {candidate.matchScore}%
                                      </Badge>
                                      {passesFilters && getScoreColor(candidate.matchScore || 0).startsWith('text-green') ? (
                                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                                      ) : (
                                        <AlertCircle className={`h-4 w-4 flex-shrink-0 ${passesFilters ? 'text-red-600' : 'text-gray-500'}`} />
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-col space-y-3">
                          {applyStatus && (
                            <div
                              className={`rounded-md px-3 py-2 text-sm ${applyStatus.type === 'success'
                                ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200'
                                : applyStatus.type === 'warning'
                                  ? 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200'
                                  : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-200'
                                }`}
                              role="status"
                              aria-live="polite"
                            >
                              {applyStatus.message}
                            </div>
                          )}
                          <div className="flex justify-end space-x-3">
                            {matchingResults.length === 0 ? (
                              <>
                                <Button
                                  onClick={handleStartMatching}
                                  disabled={matching || appliedCount === 0}
                                  className="flex items-center space-x-2"
                                >
                                  <Brain className="h-4 w-4" />
                                  <span>
                                    {matching ? t('aiMatching.matching', 'Matching...') : t('aiMatching.startMatching', 'Start Matching')}
                                  </span>
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="outline"
                                  onClick={handleCancel}
                                  disabled={isApplying}
                                >
                                  {t('common.cancel', 'Cancel')}
                                </Button>
                                <Button
                                  onClick={handleApplyMatching}
                                  disabled={isApplying}
                                  className="flex items-center space-x-2"
                                >
                                  {isApplying ? (
                                    <>
                                      <RefreshCw className="h-4 w-4 animate-spin" />
                                      <span>{t('aiMatching.applyingResults', 'Applying results...')}</span>
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="h-4 w-4" />
                                      <span>{t('aiMatching.applyResults', 'Apply Results')}</span>
                                    </>
                                  )}
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for applying results when no candidates exceed score */}
      <Modal
        isOpen={showConfirmApplyModal}
        onClose={() => setShowConfirmApplyModal(false)}
        title={t('aiMatching.confirmApplyTitle', 'Confirm Application')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {confirmApplyMode === 'top_k_tie'
              ? `Plusieurs candidats ont le même score (${topKTieCandidates[0]?.matchScore ?? 0}%), à la limite du Top-${confirmApplyTopK}. Choisissez le candidat qui passe à l'étape suivante.`
              : confirmApplyMode === 'top_k'
                ? t('aiMatching.confirmApplyNoTopK', {
                  k: confirmApplyTopK,
                  defaultValue: `No candidates are available for the Top-${confirmApplyTopK} selection. Do you still want to apply the results?`
                })
                : t('aiMatching.confirmApplyNoQualified', {
                  score: confirmApplyScore,
                  defaultValue: `No candidates exceed the minimum score of ${confirmApplyScore}%. Do you still want to apply the results?`
                })}
          </p>
          {confirmApplyMode === 'top_k_tie' && (
            <div className="grid gap-2">
              <label className="flex cursor-pointer items-center gap-3 rounded-md border border-input p-3 font-medium">
                <input
                  type="checkbox"
                  checked={areAllTopKTieCandidatesSelected}
                  onChange={() => setTopKTieChoices(previous => (
                    areAllTopKTieCandidatesSelected
                      ? new Set(
                        Array.from(previous).filter(candidateId => !topKTieCandidateIds.includes(candidateId))
                      )
                      : new Set([...Array.from(previous), ...topKTieCandidateIds])
                  ))}
                />
                <span>
                  {areAllTopKTieCandidatesSelected
                    ? t('common.deselectAll', 'Deselect All')
                    : t('common.selectAll', 'Select All')}
                </span>
              </label>
              {topKTieCandidates.map(candidate => (
                <label
                  key={candidate.id}
                  className="flex cursor-pointer items-center gap-3 rounded-md border border-input p-3"
                >
                  <input
                    type="checkbox"
                    checked={topKTieChoices.has(candidate.id)}
                    onChange={() => setTopKTieChoices(previous => {
                      const next = new Set(previous)
                      if (next.has(candidate.id)) next.delete(candidate.id)
                      else next.add(candidate.id)
                      return next
                    })}
                  />
                  <span>{candidate.name || candidate.email || `Candidate ${candidate.id}`} ({candidate.matchScore ?? 0}%)</span>
                </label>
              ))}
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setShowConfirmApplyModal(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={applyMatchingResults} disabled={isApplying || (confirmApplyMode === 'top_k_tie' && topKTieChoices.size < topKTieRequiredChoices)}>
              {isApplying ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t('aiMatching.applyingResults', 'Applying results...')}
                </>
              ) : (
                t('common.confirm', 'Confirm')
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Zero Weights Warning Modal */}
      <Modal
        isOpen={showZeroWeightsWarning}
        onClose={() => setShowZeroWeightsWarning(false)}
        title={t('common.warning', 'Warning')}
      >
        <div className="space-y-4">
          <div className="flex items-start space-x-3 text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-medium">
              {t('offers.messages.cannotPublishZeroWeights', "Il n’est pas possible d’activer l’offre si tous les poids des champs sont à 0, Au moins un poids doit être différent de 0. Veuillez d'abord modifier les poids avant d'activer l'offre.")}
            </p>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => setShowZeroWeightsWarning(false)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {t('common.ok', 'OK')}
            </Button>
          </div>
        </div>
      </Modal>

      <RecommendedSkillsModal
        isOpen={showRecommendedSkillsModal}
        onClose={() => {
          setShowRecommendedSkillsModal(false)
          setPublishedOfferForSkills(null)
        }}
        onAccept={handleAcceptRecommendedSkills}
        skills={recommendedSkillsData}
        isLoading={isSavingRecommendedSkills}
        overlayClassName="z-[120]"
        initialWeights={{
          hard_skills: publishedOfferForSkills?.ai_matching_weights?.hard_skills ?? 2,
          soft_skills: publishedOfferForSkills?.ai_matching_weights?.soft_skills ?? 2,
          nice_to_have: publishedOfferForSkills?.ai_matching_weights?.nice_to_have ?? 0,
        }}
        initialSkillWeights={publishedOfferForSkills?.skill_weights}
      />
    </>
  )
}
